import type {
  NavigationGuardNext,
  RouteLocationNormalizedGeneric,
  RouteLocationNormalizedLoadedGeneric
} from 'vue-router'
import { useStore } from '@/stores'
import { initialize } from '@/router/middlewares/initialize'

/**
 * DTA-1092 childcare release gate. Runs the normal `initialize` middleware
 * first (so `checkout_info` — and with it `district.childcareEnabled` — is
 * loaded), then sends the family to the dashboard when Childcare is off:
 *   - no family district has Childcare on, or
 *   - the route names a district (`:districtId`) that is off, or
 *   - the route names a student (`:studentAccountId`) whose district is off.
 */
export const childcareGate = async (
  to: RouteLocationNormalizedGeneric,
  from: RouteLocationNormalizedLoadedGeneric,
  next: NavigationGuardNext
): Promise<void> => {
  let proceeded = false
  let forwarded: unknown
  await initialize(to, from, (arg?: unknown) => {
    proceeded = true
    forwarded = arg
  })
  // `initialize` either left for the lobby (never called next) or forwarded
  // something other than a plain continue — honour that untouched.
  if (!proceeded) return
  if (forwarded !== undefined) {
    next(forwarded as Parameters<NavigationGuardNext>[0])
    return
  }

  if (isChildcareRouteAllowed(to)) {
    next()
  } else {
    next({ name: 'Dashboard', replace: true })
  }
}

const isChildcareRouteAllowed = (to: RouteLocationNormalizedGeneric): boolean => {
  const store = useStore()
  if (!store.anyChildcareOn) return false

  const { districtId, studentAccountId } = to.params
  if (districtId !== undefined && !store.childcareOn(Number(districtId))) return false

  if (studentAccountId !== undefined) {
    const student = store.students.find(s => s.studentAccountId === Number(studentAccountId))
    const family = student
      ? store.families.find(f => f.familyAccountId === student.familyAccountId)
      : undefined
    // An unknown student is left to the page's own not-found handling; a
    // known student in a district with Childcare off is turned away.
    if (family && !store.childcareOn(family.districtId)) return false
  }

  return true
}
